import type { Account } from "@baton/api";
import { sidebarClientCountriesKey } from "@baton/core";
import { messageKey, useI18n } from "@baton/i18n";
import { Handshake, ListTodo, LibraryBig, MailQuestion } from "@baton/icons";
import { ErrorState } from "@baton/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { accountsQuery, NO_ACCOUNT_FILTERS } from "../../accounts/list/query.ts";
import { unassignedEmailsQuery } from "../../m365/unassigned/query.ts";
import { problemOf } from "../../app/problems.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import type { ModeDestination, ModeSidebarProps } from "../modes/modes.tsx";
import { GlideList } from "./GlideList.tsx";
import { EntriesPlaceholder } from "./placeholders.tsx";
import { RegionsSection } from "./RegionsSection.tsx";
import { SIDEBAR_PILL, SidebarItem, SidebarSection } from "./Sidebar.tsx";

/**
 * 020, `contracts/screens.md` § 020 Accounts and pipeline, the Clients mode, as the block of
 * 2026-09-27 simplifies it (The Clients sidebar) and the owner's decision of the same day on the
 * Clients sidebar separates it: two kinds of rows that never read as one list. At the top the
 * navigation group, the mode's screens with their icons: Clients, with the count of the clients the
 * reader can see, opens the client list, which takes the layout, list or board, the reader last chose
 * there (`accounts/list/layout.ts`); under it Unassigned emails (022 FR-022-236, the owner's decision
 * of 2026-09-28), the intake emails Baton could not put on one client, with their count, the total the
 * list screen's own read states on its first page, from the same cache entry; a reader the read
 * refuses sees the row with no count and the screen's own refusal; and between the two the Library (022, the owner's request
 * of 2026-09-29), the Team's shared SharePoint folders as tabs, with no count, since a folder's contents are read
 * live and never counted in Baton. The Owed row went with the customer requests and the Reports row
 * with the Reports page, both by the owner's decisions of 2026-09-27, and the Deals row with the deal
 * records, by the owner's decision of 2026-09-28 that the clients by milestone are the client list's
 * board. Client groups is not a row: the group list opens from the client list's header and the
 * palette, which lists it among the mode's destinations with the client board.
 *
 * Then, after the sidebar's section gap and with no rule, every client of the Team the reader can
 * see (the owner's decisions of the same day: the organisation owns the client, not a person, and
 * the list had no heading of its own until the label of 2026-09-29 below), grouped by country (the owner's decision of 2026-09-28,
 * which replaced the headings by client group; client groups stay for the families of operators
 * that span countries, and the client list groups by them from Display): each country is the small
 * collapsible heading over its clients, its flag and its name in the reader's locale with their
 * count, the countries by name and the clients under each by name, in the order the client list and
 * the client board give the same grouping (`accounts/grouping.ts`). A client whose country is not
 * known is located by a region (020 FR-020-262, the owner's decision of 2026-09-28) and sits under a
 * heading of that area, the globe and "Africa (region)", after every country, the areas by name. The
 * headings are the folders of the list and are drawn however few there are, one country included
 * (the QA call of 2026-09-28: a Team's only client in a country was listed with no folder of that
 * country above it and read as missing from it). The rows are a list and not more navigation: each is the client's
 * name with no icon, and no flag, which its heading carries. Which headings are collapsed is kept
 * per workspace (`sidebarClientCountriesKey`). The clients are the client list's own unfiltered read,
 * every page of it, so the count, this list and the client list share one cache entry. The list takes
 * all of the sidebar's height left under the navigation group and scrolls on its own only once its
 * rows are taller than that room, so the navigation group stays in place (the owner's decision of
 * 2026-09-28, `../shell.css`). The list is absent
 * while the Team has no client the reader can see.
 *
 * Both sections carry the uppercase label Home's sections carry (2026-09-29, by the owner's review:
 * "these should have titles like our home page has titles in the sidebar"): OVERVIEW over the
 * navigation group, and REGIONS over the list (the owner's request of 2026-09-30, "rename it to
 * region", which replaced LOCATIONS), whose headings are each a client's country or, where the country
 * is not known, its region (020 FR-020-262). Neither label is "Clients", which the rail's tile already
 * says and which the owner's decision of 2026-09-27 kept off the sidebar's head; nor "Groups", which is
 * what client groups are called. The Regions label ends in one quiet control (the same request, "add a
 * collapse and expand all thing"): Collapse all while any heading is open, Expand all once every one is
 * collapsed (`../../accounts/collapseAll.tsx`), which writes every heading's remembered state at once.
 * The section is `RegionsSection.tsx`, which the Tickets mode's sidebar draws over its own clients.
 */
/** The mode's destinations for the palette: the navigation group's rows, and Client groups and the client board, which have none. */
export const CLIENTS_DESTINATIONS: readonly ModeDestination[] = [
  { id: "accounts", label: messageKey("nav.clients.all"), path: "clients", area: "clients" },
  { id: "client-items", label: messageKey("nav.clients.clientItems"), path: "client-tasks", area: "clients" },
  { id: "library", label: messageKey("nav.clients.library"), path: "library", area: "clients" },
  { id: "unassigned-emails", label: messageKey("nav.clients.unassignedEmails"), path: "unassigned-emails", area: "clients" },
  { id: "account-groups", label: messageKey("nav.accountGroups"), path: "client-groups", area: "clients" },
  { id: "client-board", label: messageKey("account.nav.clientBoard"), path: "clients?layout=board", area: "clients" },
];

export function ClientsSidebar({ ws, onNavigate }: ModeSidebarProps) {
  const { t } = useI18n();
  const runtime = useRuntime();
  const { pathname } = useLocation();
  // The client list's own unfiltered read, every page of it: the count, the list below and the client list share one cache entry.
  const everyone = useInfiniteQuery(accountsQuery(runtime, ws, NO_ACCOUNT_FILTERS));
  const clientCount = everyone.data?.pages[0]?.total;
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = everyone;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);
  // The Unassigned emails screen's own read, whose first page states the whole list's total.
  const unassigned = useInfiniteQuery(unassignedEmailsQuery(runtime, ws));
  const unassignedCount = unassigned.data?.pages[0]?.total;
  const clients = useMemo(() => (everyone.data?.pages ?? []).flatMap((page) => page.items), [everyone.data]);
  const navigateProps = onNavigate === undefined ? {} : { onNavigate };
  const clientRow = (client: Account) => {
    const to = `/${ws}/clients/${client.id}`;
    return <SidebarItem key={client.id} to={to} label={client.name} current={pathname === to || pathname.startsWith(`${to}/`)} nested {...navigateProps} />;
  };
  return (
    <>
      <SidebarSection id="clients-overview" title={t("nav.clients.section.overview")}>
        <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL} data-sidebar-destinations>
          <SidebarItem to={`/${ws}/clients`} label={t("nav.clients.all")} icon={<Handshake />} end {...(clientCount === undefined ? {} : { count: clientCount, countLabel: t("nav.clients.clientCount", { count: clientCount }) })} {...navigateProps} />
          <SidebarItem to={`/${ws}/client-tasks`} label={t("nav.clients.clientItems")} icon={<ListTodo />} {...navigateProps} />
          <SidebarItem to={`/${ws}/library`} label={t("nav.clients.library")} icon={<LibraryBig />} {...navigateProps} />
          <SidebarItem to={`/${ws}/unassigned-emails`} label={t("nav.clients.unassignedEmails")} icon={<MailQuestion />} {...(unassignedCount === undefined ? {} : { count: unassignedCount, countLabel: t("nav.clients.unassignedCount", { count: unassignedCount }) })} {...navigateProps} />
        </GlideList>
      </SidebarSection>
      {everyone.isError && everyone.data === undefined ? (
        <div className="b-sidebar__section">
          <ErrorState form="inline" problem={problemOf(everyone.error)} onRetry={() => void everyone.refetch()} />
        </div>
      ) : everyone.isPending ? (
        <EntriesPlaceholder />
      ) : (
        <RegionsSection id="clients-regions" ws={ws} clients={clients} collapsedKey={sidebarClientCountriesKey} row={clientRow} />
      )}
    </>
  );
}
